import { useEffect } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { ExternalInsurerLogo } from '@/features/CrossSell/components/ExternalInsurerLogo/ExternalInsurerLogo'
import { usePriceIntentValueOrThrow } from '@/features/priceCalculator/usePriceIntent'
import { useTracking } from '@/services/Tracking/useTracking'
import { Button } from 'ui/src/components/Button/Button'
import { BaseDialog } from 'ui/src/components/Dialog/BaseDialog'
import { HedvigAppIcon } from 'ui/src/components/HedvigAppIcon/HedvigAppIcon'
import { LoadingDots } from 'ui/src/components/LoadingDots/LoadingDots'
import { Text } from 'ui/src/components/Text/Text'
import { CheckIcon } from 'ui/src/icons/CheckIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { useFetchInsurancePrompt } from './useFetchInsurancePrompt'
import { companyLogoShadow } from './FetchInsurancePrompt.css'

type Props = {
  company: string
  insurelyId: string | null | undefined
  onConfirm: () => void
  onSkip: () => void
}

export function FetchInsurancePrompt({ company, insurelyId, onConfirm, onSkip }: Props) {
  const { t } = useTranslation('purchase-form')
  const { state } = useFetchInsurancePrompt()
  const priceIntent = usePriceIntentValueOrThrow()
  const tracking = useTracking()

  useEffect(() => {
    if (state === 'OPEN') {
      tracking.reportInsurelyPrompted({
        id: priceIntent.product.id,
        displayNameFull: priceIntent.product.displayNameFull,
      })
    }
  }, [state, tracking, priceIntent])

  return (
    <BaseDialog.Root open={state === 'OPEN'} onOpenChange={onSkip}>
      <BaseDialog.Overlay frosted>
        <BaseDialog.Content
          maxWidth="22.5rem"
          className={yStack({
            gap: 'md',
            textAlign: 'center',
            paddingInline: 'md',
            paddingTop: 'xxl',
            paddingBottom: 'lg',
          })}
        >
          <div
            className={xStack({
              justifyContent: 'center',
              gap: 'md',
              paddingBottom: 'lg',
            })}
          >
            <ExternalInsurerLogo
              insurelyId={insurelyId}
              displayName={company}
              radius="xxl"
              size="4.5rem"
              className={companyLogoShadow}
              dashed
            />
            <LoadingDots />
            <HedvigAppIcon
              radius="xxl"
              size="4.5rem"
              badgeIcon={<CheckIcon size="0.75rem" />}
              className={companyLogoShadow}
            />
          </div>

          <div className={clsx(yStack({ paddingBottom: 'xl' }))}>
            <BaseDialog.Title align="center" variant="standard.18">
              {t('FETCH_INSURANCE_PROMPT_TITLE')}
            </BaseDialog.Title>
            <BaseDialog.Description balance>{t('FETCH_INSURANCE_PROMPT_BODY', { company })}</BaseDialog.Description>
          </div>

          <div className={yStack({ gap: 'md' })}>
            <div className={yStack({ gap: 'xs' })}>
              <Button size="large" onClick={onConfirm} fullWidth>
                {t('FETCH_INSURANCE_PROMPT_BUTTON_CONFIRM')}
              </Button>
              <Button size="large" variant="secondary" onClick={onSkip} fullWidth>
                {t('FETCH_INSURANCE_PROMPT_BUTTON_SKIP')}
              </Button>
            </div>
          </div>

          <Text size="xxs" color="textSecondary">
            {t('FETCH_INSURANCE_PROMPT_INSURELY_DISCLAIMER')}
          </Text>
        </BaseDialog.Content>
      </BaseDialog.Overlay>
    </BaseDialog.Root>
  )
}
